import { SkeletonLoader } from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { CitationsSection } from '~/components/citations-section/CitationsSection';
import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { TextSection } from '~/components/text-section/TextSection';
import { ImpactedCompanies } from '~/modules/impacted-companies/ImpactedCompanies';
import { ImpactedCountriesAndRegions } from '~/modules/impacted-countries-and-regions/ImpactedCountriesAndRegions';
import { ThematicFocus } from '~/modules/thematic-focus/ThematicFocus';
import { useCatalystInsightDetails } from '~/react-query-hooks/insights/useCatalystInsights';
import { CatalystDrawerHeader } from '~/widgets/catalyst-insights-drawer/catalyst-drawer-header/CatalystDrawerHeader';

import styles from './MarketCatalystWidget.module.scss';

export interface MarketCatalystWidgetProps {
  insightId: string;
  expandCitations: boolean;
}

export const MarketCatalystWidget = ({
  insightId,
  expandCitations = false,
}: MarketCatalystWidgetProps) => {
  const { t } = useTranslation(['insights']);
  const {
    data: catalystInsight,
    isLoading,
    error,
  } = useCatalystInsightDetails(insightId, !!insightId);

  if (error) {
    throw error;
  }

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '700px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!catalystInsight) {
    return null;
  }

  return (
    <ErrorBoundaryWrapper>
      <ErrorBoundaryWrapper>
        <CatalystDrawerHeader
          identifier={catalystInsight.identifier}
          catalystInsight={catalystInsight}
        />
      </ErrorBoundaryWrapper>
      <div className={styles.sectionGroupsWrapper}>
        {catalystInsight.section_groups.map((group, gi) => (
          <div
            key={`${group.title}-${gi}`}
            className={styles.groupWrapper}
            data-testid="section-group-wrapper"
          >
            <div className={styles.sectionGroupTitle}>{group.title}</div>
            {group.sections.map((section, si) => (
              <TextSection
                key={`${section.title}-${si}`}
                title={section.title}
                textContent={section.content ?? []}
                variant="secondary"
                useBulletPoints
              />
            ))}
          </div>
        ))}
        <div>
          <ThematicFocus
            themes={catalystInsight.themes}
            otherThemes={catalystInsight.other_themes}
            title={t('insights:catalyst.thematicFocus')}
            withPill
          />
        </div>
        <ImpactedCountriesAndRegions
          impactedCountries={catalystInsight.impacted_countries}
          impactedRegions={catalystInsight.impacted_regions}
          title={t('insights:catalyst.mostImpactedRegions')}
        />
        <ImpactedCompanies
          impactedCompanies={catalystInsight.impacted_companies}
          variant="secondary"
          title={t('insights:catalyst.mostImpactedCompanies')}
          insightCreatedAt={catalystInsight.created_at}
          hideRelationship
        />
        {!!catalystInsight.citations.length && (
          <div className={styles.citationsSectionWrapper}>
            <div className={styles.citationsTitle}>
              {t('insights:catalyst.citations')}
            </div>
            <CitationsSection
              count={catalystInsight.citations.length}
              items={catalystInsight.citations}
              title={t('insights:catalyst.webLinks')}
              isExpanded={expandCitations}
            />
          </div>
        )}
      </div>
    </ErrorBoundaryWrapper>
  );
};
